import { CreateTimeSlotDefinitionDaysOfWeekEnum } from '@/generated/openapi'
import { Validation } from '@vuelidate/core'
import { required, between, minLength, maxLength } from '@vuelidate/validators'
import { asDateTime, now } from './dateTimeUtils'

const JPG_AND_PNG_TYPES = [
  'image/jpeg',
  'image/png'
]
const IMAGE_MAX_SIZE_MB = 0.5
const IMAGE_MAX_SIZE_BYTE = IMAGE_MAX_SIZE_MB * 1024 * 1024

const ICAL_TYPES = [
  'text/calendar'
]
const ICAL_MAX_SIZE_MB = 5
const ICAL_MAX_SIZE_BYTE = ICAL_MAX_SIZE_MB * 1024 * 1024

function requiredInteger(value: number): boolean {
  if (typeof value === 'number' && Number.isInteger(value)) {
    return true
  }
  return false
}

function isValidHour(val: number|undefined) {
  return typeof val === 'number' && Number.isInteger(val) && val >= 0 && val <= 23
}

function isValidMinute(val: number|undefined) {
  return typeof val === 'number' && Number.isInteger(val) && val >= 0 && val <= 59
}

function requiredTime(value: string) {
  if (typeof value === 'string' && isValidHour(parseInt(value.split(':')[0])) && isValidMinute(parseInt(value.split(':')[1]))) {
    return true
  }
  return false
}

function requiredWeekDay(weekDays: Set<CreateTimeSlotDefinitionDaysOfWeekEnum>) {
  return weekDays && weekDays.size > 0
}

function isDateString(value: string): boolean {
  return !value || asDateTime(value).isValid || false
}

function isAfterStartTime(value: string|undefined, vm: Validation) : boolean {
  const startTime = vm.startTime || ''
  const endTime = value || ''
  return new Date('2020-01-01 ' + endTime) > new Date('2020-01-01 ' + startTime)
}

function isAfterStartDate(value: string|undefined, vm: Validation) : boolean {
  const startDate = vm.startDate || ''
  const endDate = value || ''
  return new Date(''+ endDate) >= new Date('' + startDate)
}

function requiredFutureDate(value: string|undefined): boolean {
  return !!value && now() < asDateTime(value)
}

function isTelephoneString(value?: string) {
  if (!value) {
    return true
  }
  return /^[0-9+]*$/gi.test(value)
}


function imageJpegOrPng(file: File|undefined): boolean {
  if (!file || !file.name) {
    return true;
  }
  return JPG_AND_PNG_TYPES.includes(file.type);
}

function imageMaxSize(file: File|undefined): boolean {
  if (!file || !file.name) {
    return true;
  }
  return IMAGE_MAX_SIZE_BYTE >= file.size;
}

function imageRequired(file: File|undefined): boolean {
  return file && file.size > 0 || false
}

function icalRequired(file: File|undefined): boolean {
  return file && file.size > 0 && ICAL_TYPES.includes(file.type) || false
}

function icalMaxSize(file: File|undefined): boolean {
  if (!file || !file.name) {
    return true;
  }
  return ICAL_MAX_SIZE_BYTE >= file.size;
}

function privacyNoticeRequired(val: any) {
  return val === true
}

function createRoomValidations() {
  return {
    name: {
      required,
      minLength: minLength(3),
      maxLength: maxLength(50)
    },
    description: {
      required,
      minLength: minLength(3),
      maxLength: maxLength(100)
    },
    capacity: {
      requiredInteger,
      between: between(0, 99999)
    },
    openStartValue: {
      requiredInteger,
      between: between(0, 365)
    },
    openStartType: {
    },
    openEndValue: {
      requiredInteger,
      between: between(0, 365)
    },
    openEndType: {
    },
  }
}

function createHolidayValidations() {
  return {
    name: {
      maxLength: maxLength(100),
      required
    },
    description: {
      maxLength: maxLength(500)
    },
    date: {
      required
    }
  }
}

export {
  createRoomValidations,
  createHolidayValidations,
  requiredInteger,
  requiredTime,
  isDateString,
  requiredWeekDay,
  isAfterStartTime,
  isAfterStartDate,
  requiredFutureDate,
  imageJpegOrPng,
  imageMaxSize,
  imageRequired,
  icalRequired,
  icalMaxSize,
  isTelephoneString,
  privacyNoticeRequired,
  IMAGE_MAX_SIZE_MB,
  ICAL_MAX_SIZE_MB,
}
